import { FormLines, HasIdAndName, NestedOption, TreeSelectField, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo } from "react";
import { SaveOptionPricingConfigVariantIncrementalInput } from "src/generated/graphqlTypes";
import { BoundOptionPricingConfigFields } from "src/routes/admin/components/BoundOptionPricingConfigFields";
import { GlobalOptionForm_MarketWithCommunityFragment } from "src/routes/libraries/global-options-v2/GlobalOptionForm.generated";
type BoundOptionPricingConfigVariantProps = {
  row: ObjectState<SaveOptionPricingConfigVariantIncrementalInput>;
  targets: GlobalOptionForm_MarketWithCommunityFragment[];
  isStructural?: boolean;
};

export function BoundOptionPricingConfigVariants({ row, isStructural, targets }: BoundOptionPricingConfigVariantProps) {
  const hasNoTargets = useComputed(() => Boolean((row.projectIds.value ?? []).isEmpty), [row.projectIds]);
  return (
    <FormLines>
      <OptionPricingConfigVariantsTreeSelect targets={targets} row={row} />
      <BoundOptionPricingConfigFields
        postCutoffDesiredMarginInBasisPoints={row.postCutoffDesiredMarginInBasisPoints}
        preCutoffDesiredMarginInBasisPoints={row.preCutoffDesiredMarginInBasisPoints}
        selectionFeeInCents={row.selectionFeeInCents}
        minimumPriceInCents={row.minimumPriceInCents}
        reasonNote={row.reasonNote}
        isStructural={isStructural}
        disabled={hasNoTargets && "Select a Market or Community"}
      />
    </FormLines>
  );
}

function OptionPricingConfigVariantsTreeSelect({
  targets,
  row,
}: Pick<BoundOptionPricingConfigVariantProps, "targets" | "row">) {
  // Format markets and communities for TreeSelect
  const options: NestedOption<HasIdAndName>[] = useMemo(() => {
    return targets.map((market) => ({
      id: market.id,
      name: market.name,
      children: market.children.map((project) => ({
        id: project.id,
        name: project.name,
      })),
    }));
  }, [targets]);
  const values = useComputed(() => row.projectIds.value ?? [], [row.projectIds]);
  return (
    <TreeSelectField
      label="Markets and Communities"
      labelStyle="hidden"
      placeholder="All Markets"
      values={values}
      options={options}
      onSelect={({ root, leaf }) => row.projectIds.set(root.values)}
    />
  );
}
